// Lead Generation System — staff Prelead API client. Thin typed wrappers over
// the `api/preleads` controller (reads gate on LeadGen.View; mutations on
// LeadGen.Manage). Mirrors the shape of `@/api/leads`. Phoenix is the Loan
// Administrator — a Prelead is a business-for-sale contact a generator sourced,
// never a brokerage relationship.

import { api } from "@/api/client";
import type { PagedQuery, PagedResult } from "@/types/api";

// --- Enum string unions (System.Text.Json serializes enum names) ----------

export type PreleadStatus =
  | "Draft"
  | "MessageSent"
  | "ResponseReceived"
  | "NdaRequested"
  | "NdaSubmitted"
  | "CimRequested"
  | "CimReceived"
  | "Interested"
  | "Escalated"
  | "Contacted"
  | "BadFit"
  | "Duplicate"
  | "NoResponse"
  | "Closed"
  | "Disqualified";

export type PreleadSource =
  | "BizBuySell"
  | "DealStream"
  | "LoopNet"
  | "BrokerWebsite"
  | "LinkedIn"
  | "ColdOutreach"
  | "Referral"
  | "Marketplace"
  | "Other";

export type PreleadNdaStatus = "NotRequested" | "Requested" | "Completed";
export type PreleadCimStatus = "NotRequested" | "Requested" | "Received";
export type PreleadInterestLevel = "Unknown" | "Cold" | "Warm" | "Hot";

// The four INDEPENDENT milestone tracks; the headline PreleadStatus is derived
// server-side as the furthest milestone reached.
export type PreleadOutreachStatus = "None" | "MessageSent" | "ResponseReceived";
export type PreleadInterestStatus = "None" | "Interested";

export type PreleadActivityType =
  | "PreleadCreated"
  | "StatusChanged"
  | "NoteAdded"
  | "MessageSent"
  | "ResponseReceived"
  | "NdaRequested"
  | "NdaCompleted"
  | "CimRequested"
  | "CimReceived"
  | "MarkedInterested"
  | "EscalatedToLead"
  | "FileUploaded"
  | "AiExtracted"
  | "Reassigned"
  | "Archived";

// --- DTOs ------------------------------------------------------------------

export interface PreleadSummaryDto {
  id: string;
  leadGeneratorId: string;
  businessName?: string | null;
  contactFirstName?: string | null;
  contactLastName?: string | null;
  phone?: string | null;
  email?: string | null;
  city?: string | null;
  state?: string | null;
  industry?: string | null;
  askingPrice?: number | null;
  cashFlow?: number | null;
  source: PreleadSource;
  status: PreleadStatus;
  interestLevel: PreleadInterestLevel;
  ndaStatus: PreleadNdaStatus;
  cimStatus: PreleadCimStatus;
  lastContactDate?: string | null;
  nextFollowUpDate?: string | null;
  /** Set once the Prelead has been escalated into the Leads pipeline. */
  escalatedToLeadId?: string | null;
  isArchived: boolean;
  createdAt: string;
  // Four-track sub-states (the headline `status` is derived from these).
  outreachStatus: PreleadOutreachStatus;
  interestStatus: PreleadInterestStatus;
  // Confirmed funnel outcomes (green chips) fed back from the escalated Lead.
  confirmedContactedAtUtc?: string | null;
  confirmedInterestedAtUtc?: string | null;
  // Running per-prelead earned total = SUM of every credited state. Display-only.
  totalEarned: number;
  // Fallback-label inputs when businessName is blank (empty/partial captures are
  // normal): the listing URL + a short first-line snippet of the raw paste.
  listingUrl?: string | null;
  rawTextSnippet?: string | null;
}

export interface PreleadDetailDto {
  id: string;
  leadGeneratorId: string;
  businessName?: string | null;
  contactFirstName?: string | null;
  contactLastName?: string | null;
  title?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  industry?: string | null;
  askingPrice?: number | null;
  cashFlow?: number | null;
  listingUrl?: string | null;
  brokerName?: string | null;
  brokerCompany?: string | null;
  brokerEmail?: string | null;
  brokerPhone?: string | null;
  source: PreleadSource;
  sourceDetail?: string | null;
  status: PreleadStatus;
  interestLevel: PreleadInterestLevel;
  notes?: string | null;
  lastContactDate?: string | null;
  nextFollowUpDate?: string | null;
  // --- Outreach track ---
  outreachStatus: PreleadOutreachStatus;
  messageSentAtUtc?: string | null;
  responseReceivedAtUtc?: string | null;
  // --- Interest track ---
  interestStatus: PreleadInterestStatus;
  interestedAtUtc?: string | null;
  ndaStatus: PreleadNdaStatus;
  ndaRequestedAtUtc?: string | null;
  ndaCompletedAtUtc?: string | null;
  ndaDocumentId?: string | null;
  cimStatus: PreleadCimStatus;
  cimRequestedAtUtc?: string | null;
  cimReceivedAtUtc?: string | null;
  cimDocumentId?: string | null;
  rawPastedText?: string | null;
  capturedLinksJson?: string | null;
  aiSummary?: string | null;
  aiFitFlagsJson?: string | null;
  aiModel?: string | null;
  aiExtractedAtUtc?: string | null;
  escalatedToLeadId?: string | null;
  escalatedAtUtc?: string | null;
  isArchived: boolean;
  createdAt: string;
  modifiedAt?: string | null;
  // Confirmed funnel outcomes (green chips) fed back from the escalated Lead.
  confirmedContactedAtUtc?: string | null;
  confirmedInterestedAtUtc?: string | null;
}

/** One credited ledger row for a prelead. `amount` is the row's CreditedAmount
 *  (so the line items sum to {@link PreleadEarningsDto.total}); `state` is the
 *  earning state string (Estimated / PendingReview / Approved / Adjusted). */
export interface PreleadEarningLineDto {
  kpiCode: string;
  label: string;
  amount: number;
  state: string;
  approvedAtUtc?: string | null;
}

/** Per-prelead compensation breakdown: the running earned total + one line per
 *  credited ledger row. Standalone / display-only — nothing here moves money. */
export interface PreleadEarningsDto {
  total: number;
  lineItems: PreleadEarningLineDto[];
}

export interface PreleadActivityDto {
  id: string;
  preleadId: string;
  activityType: PreleadActivityType;
  description: string;
  metadataJson?: string | null;
  performedByUserId: string;
  timestamp: string;
}

export interface PreleadStatsDto {
  total: number;
  byStatus: Record<PreleadStatus, number>;
}

/** Per-card "this pay week" counts that reconcile with the Earnings Review. The
 *  earning-bearing counts (messageSent/escalated/ndaCim/appointment) tie out to the
 *  Earnings bucket counts for the resolved pay week; the non-earning counts
 *  (response/interested/contacted) are windowed by the milestone stamp (activity
 *  only). ndaCimAllTime/appointmentAllTime are all-time ledger counts for the two
 *  earning-only cards that have no headline-status equivalent. */
export interface PreleadWindowedStatsDto {
  weekOf: string;
  messageSent: number;
  escalated: number;
  ndaCim: number;
  appointment: number;
  response: number;
  interested: number;
  contacted: number;
  ndaCimAllTime: number;
  appointmentAllTime: number;
}

/** One append-only note on a Prelead's thread (oldest-first). `source` is
 *  "AI" (a distilled paste summary) or "User" (typed). */
export interface PreleadNoteDto {
  id: string;
  preleadId: string;
  body: string;
  source: "AI" | "User";
  createdAtUtc: string;
  createdBy?: string | null;
}

export interface AddPreleadNoteRequest {
  body: string;
}

/** One file under the prelead's DMS folder (general attachments + the NDA/CIM
 *  milestone docs), as surfaced in the read-only staff monitor. `category` is the
 *  serialized DocumentCategory enum name. No download URL is embedded — a
 *  short-lived SAS is fetched on demand via {@link preleadsApi.attachmentDownload}. */
export interface PreleadAttachmentDto {
  docId: string;
  fileName: string;
  extension: string | null;
  sizeBytes: number;
  category: string;
  tags: string | null;
  createdAt: string;
}

/** A fresh short-lived read SAS for one prelead file (mirrors the backend
 *  DocumentDownloadDto). Declared locally — the staff surface can't import the
 *  portal's copy. */
export interface DocumentDownloadDto {
  sasUrl: string;
  expiresAt: string;
  isPdf: boolean;
}

export interface PreleadListQuery extends PagedQuery {
  leadGeneratorId?: string;
  status?: PreleadStatus;
  /** Multi-select milestone filter (AND semantics): a prelead matches only when
   *  it has reached EVERY selected token (message_sent, response_received,
   *  nda_complete, cim_received, interested, escalated, confirmed_contacted,
   *  confirmed_interested). Sent as repeated `?statuses=` params (the shared
   *  api client serializes arrays as repeated query keys). */
  statuses?: string[];
  source?: PreleadSource;
  state?: string;
  interestLevel?: PreleadInterestLevel;
  /** Only archived rows when true; omitted/false excludes archived (default). */
  archived?: boolean;
  /** ADMIN view — only soft-deleted rows when true (for restore); default hides
   *  them via the global filter. */
  deleted?: boolean;
  hasFollowUp?: boolean;
  /** Which timestamp the from/to range filters on. */
  dateField?: "created" | "updated";
  /** ISO lower/upper bounds on the chosen date field. */
  from?: string;
  to?: string;
}

export const preleadsApi = {
  list: (query?: PreleadListQuery) =>
    api.get<PagedResult<PreleadSummaryDto>>("/preleads", query),
  stats: (query?: PreleadListQuery) =>
    api.get<PreleadStatsDto>("/preleads/stats", query),
  /** Per-card "this pay week" counts (Monday-UTC) that reconcile with Earnings.
   *  weekOf = any date in the desired week (snapped server-side); omit for the
   *  current week. leadGeneratorId scopes to one generator (like Earnings). */
  windowedStats: (leadGeneratorId?: string, weekOf?: string) =>
    api.get<PreleadWindowedStatsDto>("/preleads/windowed-stats", { leadGeneratorId, weekOf }),
  get: (id: string) => api.get<PreleadDetailDto>(`/preleads/${id}`),
  /** Display-only per-prelead compensation breakdown (running total + per-credit
   *  line items). Reads gate on LeadGen.View. */
  earnings: (id: string) => api.get<PreleadEarningsDto>(`/preleads/${id}/earnings`),
  activity: (id: string) => api.get<PreleadActivityDto[]>(`/preleads/${id}/activity`),
  notes: (id: string) => api.get<PreleadNoteDto[]>(`/preleads/${id}/notes`),
  /** The unified read-only Files list (general attachments + NDA/CIM docs). */
  attachments: (id: string) => api.get<PreleadAttachmentDto[]>(`/preleads/${id}/attachments`),
  /** A fresh short-lived read SAS for one prelead file. */
  attachmentDownload: (id: string, docId: string) =>
    api.get<DocumentDownloadDto>(`/preleads/${id}/attachments/${docId}`),
  addNote: (id: string, body: string) =>
    api.post<PreleadNoteDto>(`/preleads/${id}/notes`, { body }),
  // Reassign a Prelead to another generator (LeadGen.Manage). Not surfaced on
  // the read-only staff monitor today; kept for a future console action.
  assign: (id: string, leadGeneratorId: string) =>
    api.post<PreleadDetailDto>(`/preleads/${id}/assign`, { leadGeneratorId }),
  archive: (id: string) => api.post<PreleadDetailDto>(`/preleads/${id}/archive`),
  unarchive: (id: string) => api.post<PreleadDetailDto>(`/preleads/${id}/unarchive`),
  /** Staff disqualify (LeadGen.Manage) — flips the prelead to Disqualified and
   *  voids the still-Pending escalation credit. Reason required. */
  disqualify: (id: string, reason: string, notes?: string | null) =>
    api.post<PreleadDetailDto>(`/preleads/${id}/disqualify`, { reason, notes: notes ?? null }),
  /** Admin SOFT delete (LeadGen.Delete). Hides from every read; retains the
   *  record + earnings trail; voids any still-Pending credit. */
  softDelete: (id: string) => api.post<PreleadDetailDto>(`/preleads/${id}/delete`),
  /** Admin restore of a soft-deleted prelead (LeadGen.Delete). */
  restore: (id: string) => api.post<PreleadDetailDto>(`/preleads/${id}/restore`),
};
